"use client";

import { PropsWithChildren, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { PROPERTY_FILTER_KEYS } from "@/src/const";
import { StrapiCollectionType, StrapiComponentType, StrapiType } from "@/src/type";
import { Log } from "@/src/util";

import { PropertyContext } from "./PropertyContext";

function initFilters(defaultQuery?: string) {
  const params = new URLSearchParams(defaultQuery);
  const filters: Record<string, string | string[]> = {};

  if (params.size === 0) {
    return filters;
  }

  params.forEach((value, key) => {
    // check if the key matches any of the PROPERTY_FILTER_KEYS param values
    const matchingFilterKey = Object.values(PROPERTY_FILTER_KEYS).find((filterKey) => filterKey.param === key);

    if (matchingFilterKey) {
      //handle arrays of values in the filters
      if (matchingFilterKey.type === "string[]") {
        const values = params.getAll(key);
        filters[key] = values;
      }
      //everything else is considered a single value string
      else {
        filters[key] = value;
      }
    }
  });

  return filters;
}

function getFiltersQueryString(filters: Record<string, string | string[]>) {
  const searchEntries = Object.entries(filters ?? {}).reduce<[string, string][]>((acc, [param, value]) => {
    if (value === undefined) {
      return acc;
    }

    if (Array.isArray(value)) {
      value.forEach((val) => acc.push([param, val]));
    } else {
      acc.push([param, value]);
    }

    return acc;
  }, []);

  return new URLSearchParams(searchEntries).toString();
}

interface PropertyProviderProps {
  floorplans: StrapiCollectionType.PropertyFloorplan[];
  unitCarousel?: StrapiComponentType.Object.AssetCaption[];
  externalRefId?: string;
  externalRefType?: string;
  defaultQuery?: string;
  property?: StrapiType.StrapiSingleWrapper<StrapiCollectionType.Property>;
}

function PropertyProvider({
  children,
  floorplans,
  externalRefId,
  externalRefType,
  unitCarousel,
  defaultQuery,
  property,
}: PropsWithChildren<PropertyProviderProps>) {
  const [filters, setFilters] = useState<Record<string, string | string[]>>(initFilters(defaultQuery) || {});
  const [filtersQuery, setFiltersQuery] = useState(getFiltersQueryString(filters));
  const [homesCount, setHomesCount] = useState<{ [key: string]: number }>({});
  const pathname = usePathname();

  /**
   * when filters change, update the filters query
   */
  useEffect(() => {
    setFiltersQuery(getFiltersQueryString(filters));
  }, [filters]);

  /**
   * when pathname changes, update the filters
   */
  useEffect(() => {
    setFilters(initFilters(window.location.search));
  }, [pathname]);

  /**
   * Show loaded web config
   */
  useEffect(() => {
    if (!floorplans) {
      return;
    }

    Log.logToConsoleDebug(
      "PropertyProvider.tsx",
      "Property Loaded",
      [{ externalRefId, externalRefType, unitCarousel, floorplans }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [floorplans, externalRefId, externalRefType, unitCarousel]);

  function updateFilter(key: string, value: string | string[]) {
    const delimited = "*";

    // Find filter key by param value instead of directly indexing
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const filterKeyEntry = Object.entries(PROPERTY_FILTER_KEYS).find(([_, config]) => config.param === key);

    const dataType = filterKeyEntry?.[1]?.type;
    if (!dataType) {
      return;
    }

    let toSetValue: string | null = null;
    const currentValue = filters[key];
    if (dataType === "string[]" && Array.isArray(value)) {
      toSetValue = value.join(delimited);
      const clone = JSON.parse(JSON.stringify(filters));
      if ((value ?? []).length > 0) {
        clone[key] = value;
      } else {
        delete clone[key];
      }
      setFilters(clone);
    } else if (dataType === "string" && typeof value === "string") {
      toSetValue = currentValue === value ? "" : value;
      setFilters((prev) => ({
        ...prev,
        [key]: toSetValue ?? "",
      }));
    }

    const params = new URLSearchParams(window.location.search);
    if (toSetValue) {
      params.set(key, toSetValue);
    } else {
      params.delete(key);
    }
    window.history.pushState(null, "", `?${params.toString()}`);
  }

  const clearFilters = () => {
    setFilters({});
    const params = new URLSearchParams(window.location.search);
    for (const key in filters) {
      params.delete(key);
    }
    window.history.pushState(null, "", `?${params.toString()}`);
  };

  const registerHomeCount = (key: string, count: number) => {
    setHomesCount((prev) => ({
      ...prev,
      [key]: count,
    }));
  };

  return (
    <PropertyContext.Provider
      value={{
        floorplans: Array.isArray(floorplans) ? floorplans : [],
        unitCarousel: Array.isArray(unitCarousel) ? unitCarousel : [],
        externalRefId,
        externalRefType,
        filters,
        homesCount,
        filtersQuery,
        registerHomeCount,
        updateFilter,
        clearFilters,
        property: property?.data?.attributes,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

export default PropertyProvider;
